/**
 * Login Flow Hook
 *
 * Manages the two-stage passwordless login flow:
 * 1. Email entry
 * 2. Method selection and authentication
 */
import { useState, useCallback, useEffect } from 'react';
import { oryService } from '../../../core/ory/ory-service';
import { EmailOTPService } from '../../../core/services/email-otp-service';
import { PasskeyService } from '../../../core/services/passkey-service';
import { getAuthErrorMessage } from '../utils/login-flow-utils';
import { getAuthSuccessRedirectUrl, getAuthBaseUrl } from '../../../config/auth-routes';
// Helper to check if we're in development mode (works with Vite)
const isDevelopment = () => {
    // Check Vite environment
    if (typeof window !== 'undefined' && window.import?.meta?.env?.DEV) {
        return true;
    }
    // Fallback to process.env for Node.js environments
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        return true;
    }
    return false;
};
export const useAuthFlow = () => {
    const [loginFlow, setLoginFlow] = useState(null);
    const emailOTPService = EmailOTPService.getInstance();
    const passkeyService = PasskeyService.getInstance();
    const [state, setState] = useState({
        currentStep: 'email',
        email: null,
        rememberMe: false,
        availableMethods: [],
        selectedMethod: null,
        isLoading: true, // Start with loading true while checking session
        error: null,
        isAuthenticated: false
    });
    // Initialize login flow on mount
    useEffect(() => {
        const initFlow = async () => {
            try {
                // Don't create login flow if we're on setup pages
                if (window.location.pathname.startsWith('/setup')) {
                    setState(prev => ({ ...prev, isLoading: false }));
                    return;
                }
                // First check if user already has a session
                const session = await oryService.getCurrentSession();
                if (session && session.active) {
                    // Check if we were redirected here due to a backend session failure.
                    // This prevents an infinite loop: /auth → /access (401) → /auth → ...
                    const params = new URLSearchParams(window.location.search);
                    if (params.get('error') === 'session_expired') {
                        try {
                            const logoutUrl = await oryService.logout(getAuthBaseUrl());
                            window.location.href = logoutUrl;
                        }
                        catch {
                            setState(prev => ({
                                ...prev,
                                error: 'Your session has expired. Please log in again.',
                                isLoading: false,
                            }));
                        }
                        return;
                    }
                    window.location.href = getAuthSuccessRedirectUrl();
                    return;
                }
                // No active session, create login flow
                const flow = await oryService.createLoginFlow();
                setLoginFlow(flow);
                setState(prev => ({ ...prev, isLoading: false }));
            }
            catch (error) {
                // Handle 400 error specifically - it might mean user is already logged in
                if (error.response?.status === 400) {
                    try {
                        const session = await oryService.getCurrentSession();
                        if (session && session.active) {
                            const params = new URLSearchParams(window.location.search);
                            if (params.get('error') === 'session_expired') {
                                try {
                                    const logoutUrl = await oryService.logout(getAuthBaseUrl());
                                    window.location.href = logoutUrl;
                                }
                                catch {
                                    setState(prev => ({
                                        ...prev,
                                        error: 'Your session has expired. Please log in again.',
                                        isLoading: false,
                                    }));
                                }
                                return;
                            }
                            window.location.href = getAuthSuccessRedirectUrl();
                            return;
                        }
                    }
                    catch (sessionError) {
                        // Session check failed, show error
                    }
                }
                setState(prev => ({
                    ...prev,
                    error: getAuthErrorMessage(error),
                    isLoading: false
                }));
            }
        };
        initFlow();
    }, []);
    // Initialize conditional mediation (passkey autofill) when login flow is ready
    useEffect(() => {
        const initConditionalMediation = async () => {
            if (!loginFlow) {
                return;
            }
            try {
                // Check if conditional mediation is supported
                const capabilities = await passkeyService.checkCapabilities();
                if (!capabilities.conditionalMediation) {
                    return;
                }
                // Start conditional mediation in background
                // This makes passkeys appear in the email input field dropdown
                const result = await passkeyService.authenticateWithConditionalUI(loginFlow);
                if (result.success) {
                    window.location.href = getAuthSuccessRedirectUrl();
                }
            }
            catch (error) {
                // Silently handle errors - conditional mediation is a progressive enhancement
            }
        };
        initConditionalMediation();
    }, [loginFlow, passkeyService]);
    // Store email for direct flow
    const submitEmail = useCallback(async (email, rememberMe) => {
        // In direct flow, we just store the email and don't submit to Ory yet
        setState(prev => ({
            ...prev,
            email: email,
            rememberMe: rememberMe ?? prev.rememberMe,
            currentStep: 'email' // Stay on email step
        }));
    }, []);
    // Select and execute authentication method
    const selectMethod = useCallback(async (method, email, rememberMe) => {
        // Use provided email or fall back to stored email
        const userEmail = email || state.email;
        const shouldRemember = rememberMe ?? state.rememberMe;
        if (!loginFlow) {
            setState(prev => ({ ...prev, error: 'Authentication session expired. Please refresh and try again.' }));
            return;
        }
        if (method === 'code' && !userEmail) {
            setState(prev => ({ ...prev, error: 'Email is required for OTP authentication' }));
            return;
        }
        setState(prev => ({
            ...prev,
            selectedMethod: method,
            isLoading: true,
            error: null
        }));
        try {
            let authResult;
            switch (method) {
                case 'code': {
                    // Email OTP flow - for direct flow, submit identifier and method together
                    const csrfToken = oryService.getCSRFToken(loginFlow);
                    try {
                        // Direct flow: submit both identifier and method
                        const response = await oryService.updateLoginFlow(loginFlow.id, {
                            method: 'code',
                            identifier: userEmail,
                            csrf_token: csrfToken
                        });
                        // Update flow with response
                        setLoginFlow(response);
                        setState(prev => ({
                            ...prev,
                            email: userEmail,
                            rememberMe: shouldRemember,
                            isLoading: false
                        }));
                    }
                    catch (error) {
                        // Check if this is a 400 error with flow data (which happens when code is sent)
                        if (error.response?.status === 400 && error.response?.data) {
                            const responseData = error.response.data;
                            // Check if response has UI structure (indicates flow update)
                            if (responseData.ui && responseData.id) {
                                // Update flow with the response data
                                setLoginFlow(responseData);
                                setState(prev => ({
                                    ...prev,
                                    email: userEmail,
                                    rememberMe: shouldRemember,
                                    isLoading: false
                                }));
                                // Exit early - this is success, not an error
                                return;
                            }
                        }
                        throw new Error(error.response?.data?.ui?.messages?.[0]?.text || error.message || 'Failed to send code');
                    }
                    break;
                }
                case 'passkey': {
                    // Passkey authentication - passkeys don't need the identifier
                    // as the passkey itself identifies the user
                    authResult = await passkeyService.authenticate(loginFlow, { rememberMe: shouldRemember });
                    if (authResult.success) {
                        // Redirect immediately to main application
                        window.location.href = getAuthSuccessRedirectUrl();
                    }
                    else {
                        throw new Error(authResult.error || 'Passkey authentication failed');
                    }
                    break;
                }
                default:
                    throw new Error(`Unknown authentication method: ${method}`);
            }
        }
        catch (error) {
            setState(prev => ({
                ...prev,
                error: getAuthErrorMessage(error),
                isLoading: false,
                selectedMethod: null
            }));
        }
    }, [loginFlow, state.email, state.rememberMe, emailOTPService, passkeyService]);
    // Submit OTP code for verification
    const submitCode = useCallback(async (code, rememberMe) => {
        if (!loginFlow || !state.email) {
            setState(prev => ({ ...prev, error: 'Invalid flow state' }));
            return;
        }
        const shouldRemember = rememberMe ?? state.rememberMe;
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const csrfToken = oryService.getCSRFToken(loginFlow);
            const response = await oryService.updateLoginFlow(loginFlow.id, {
                method: 'code',
                code: code,
                identifier: state.email,
                csrf_token: csrfToken,
                ...(shouldRemember && { remember: true })
            });
            if ('session' in response) {
                // Authentication successful - redirect immediately
                window.location.href = getAuthSuccessRedirectUrl();
            }
            else {
                throw new Error('Verification failed');
            }
        }
        catch (error) {
            // Check if this is a 400 error with flow data containing error messages
            if (error.response?.status === 400 && error.response?.data) {
                const responseData = error.response.data;
                // Check if response has UI structure with error messages
                if (responseData.ui?.messages && responseData.ui.messages.length > 0) {
                    // Extract the first error message
                    const errorMessage = responseData.ui.messages[0].text;
                    // Update flow with response data (might have updated state)
                    if (responseData.id) {
                        setLoginFlow(responseData);
                    }
                    setState(prev => ({
                        ...prev,
                        error: errorMessage || 'Invalid verification code',
                        isLoading: false
                    }));
                    return;
                }
            }
            // Fallback to generic error handling
            setState(prev => ({
                ...prev,
                error: getAuthErrorMessage(error),
                isLoading: false
            }));
        }
    }, [loginFlow, state.email]);
    // Clear error
    const clearError = useCallback(() => {
        setState(prev => ({ ...prev, error: null }));
    }, []);
    // Reset the flow
    const reset = useCallback(() => {
        setState({
            currentStep: 'email',
            email: null,
            rememberMe: false,
            availableMethods: [],
            selectedMethod: null,
            isLoading: false,
            error: null,
            isAuthenticated: false
        });
    }, []);
    return {
        state,
        submitEmail,
        selectMethod,
        submitCode,
        clearError,
        reset
    };
};
